import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { format, parse, startOfWeek, getDay, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Popover from '@mui/material/Popover';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { ToastContainer, toast } from 'react-toastify';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ListAltIcon from '@mui/icons-material/ListAlt';
import HistoryIcon from '@mui/icons-material/History';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NotesIcon from '@mui/icons-material/Notes';
import BusinessIcon from '@mui/icons-material/Business';
import { fetchReservas, fetchSalasPermitidas, crearReserva, actualizarReserva, eliminarReserva, fetchPersonasReservables } from '../../peticiones_API/PeticionesSalasReservas';
import { fetchPersonasData } from '../../peticiones_API/PeticionesGestionPersonasPersonas';
import { fetchNegociosData } from '../../peticiones_API/PeticionesGestionPersonasNegocios';
import { fetchEmpresasColaboradorasData } from '../../peticiones_API/PeticionesEmpresasColaboradoras';
import './GestorSalasReservas.css';

// Datos uniformes del dueño de una reserva, sea interno (Persona) o colaborador
// (PersonaColaboradora). Punto único para acceder a nombre/tipo/id/empresa.
const getReservaOwnerInfo = (reserva) => {
  const persona = reserva?.Persona || reserva?.PersonaColaboradora || null;
  return {
    tipo: reserva?.persona_colaboradora_id ? 'colaborador' : 'interno',
    id: reserva?.persona_id ?? reserva?.persona_colaboradora_id ?? null,
    nombre: persona?.nombre || '',
    apellidos: persona?.apellidos || '',
    empresaId: reserva?.empresa_id ?? reserva?.empresa_colaboradora_id ?? null,
    esEmpresaColaboradora: !!reserva?.empresa_colaboradora_id
  };
};


const locales = {
  es: es,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const DragAndDropCalendar = withDragAndDrop(Calendar);

function GestorSalasReservas() {
  const location = useLocation();
  const navigate = useNavigate();
  const urlPadre = window.location.pathname;
  
  // Leer parámetros de la URL
  const params = new URLSearchParams(location.search);
  const salaParam = params.get('sala') || '';
  
  const [reservas, setReservas] = useState([]);
  const [salas, setSalas] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [negocios, setNegociosData] = useState([]);
  const [empresasColaboradoras, setEmpresasColaboradoras] = useState([]);
  const [reservables, setReservables] = useState([]);
  const [activeTab, setActiveTab] = useState('calendario');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [filtroSala, setFiltroSala] = useState(salaParam);
  const [filtroPersona, setFiltroPersona] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverEvent, setPopoverEvent] = useState(null);
  const [nuevaReserva, setNuevaReserva] = useState({
    sala_id: '',
    empresa_id: '',
    persona_id: '',
    persona_colaboradora_id: null,
    empresa_colaboradora_id: null,
    fecha: '',
    hora_inicio: '',
    hora_fin: '',
    observaciones: ''
  });

  const currentUser = useMemo(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload;
      } catch {
        return null;
      }
    }
    return null;
  }, []);

  const esColaborador = !!currentUser?.es_persona_colaboradora;

  // Color de la reserva: colaborador -> empresas_colaboradoras; interno -> negocios.
  const getColorReserva = useCallback((reserva) => {
    if (reserva?.empresa_colaboradora_id) {
      const ec = empresasColaboradoras.find(e => e.Id === reserva.empresa_colaboradora_id);
      return ec?.color || '#95a5a6';
    }
    const negocio = negocios.find(e => e.Id === reserva?.empresa_id);
    return negocio?.color || '#95a5a6';
  }, [negocios, empresasColaboradoras]);

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      const [reservasData, salasData, personasData, negociosData, empresasColabData] = await Promise.all([
        fetchReservas(),
        fetchSalasPermitidas(),
        fetchPersonasData(),
        fetchNegociosData(),
        fetchEmpresasColaboradorasData()
      ]);
      setReservas(reservasData);
      setSalas(salasData);
      setPersonas(personasData);
      setNegociosData(negociosData); //??? setEmpresas
      setEmpresasColaboradoras(empresasColabData || []);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      alert('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  }, []);

  console.log("aaaaaa", reservas)

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  useEffect(() => {
    if (!filtroSala && salas.length > 0 && activeTab !== 'historial') {
      setFiltroSala(salas[0].Id.toString());
    }
  }, [salas, filtroSala, activeTab]);

  // Sincronizar filtroSala con URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (filtroSala) {
      params.set('sala', filtroSala);
    }
    const newUrl = params.toString() ? `${urlPadre}?${params.toString()}` : urlPadre;
    navigate(newUrl, { replace: true });
  }, [filtroSala, navigate, urlPadre]);

  const events = useMemo(() => {
    return reservas.map(reserva => {
      const horaInicio = format(parseISO(reserva.fecha_inicio), 'HH:mm');
      const horaFin = format(parseISO(reserva.fecha_fin), 'HH:mm');
      const rangoHorario = `${horaInicio}-${horaFin}`;
      const info = getReservaOwnerInfo(reserva);
      const nombreCompleto = `${info.nombre} ${info.apellidos}`.trim();

      const negocio = negocios.find(emp => emp.Id === reserva.empresa_id);
      const colorNegocio = getColorReserva(reserva);

      return {
        id: reserva.Id,
        title: nombreCompleto,
        horario: rangoHorario,
        start: parseISO(reserva.fecha_inicio),
        end: parseISO(reserva.fecha_fin),
        resource: reserva,
        color: colorNegocio,
        Negocio: negocio
      };
    });
  }, [reservas, negocios, getColorReserva]);

  const reservasFiltradas = useMemo(() => {
    const ahora = new Date();
    return reservas.filter(reserva => {
      const matchSala = !filtroSala || reserva.sala_id === parseInt(filtroSala);
      const matchPersona = !filtroPersona || reserva.persona_id === parseInt(filtroPersona);
      const esFutura = parseISO(reserva.fecha_inicio) >= ahora;
      return matchSala && matchPersona && esFutura;
    }).sort((a, b) => parseISO(a.fecha_inicio) - parseISO(b.fecha_inicio));
  }, [reservas, filtroSala, filtroPersona]);

  const reservasUsuario = useMemo(() => {
    if (!currentUser) return [];
    return reservas.filter(reserva => {
      const matchSala = !filtroSala || reserva.sala_id === parseInt(filtroSala);
      const info = getReservaOwnerInfo(reserva);
      const matchUsuario = esColaborador
        ? (info.tipo === 'colaborador' && info.id === currentUser.id)
        : (info.tipo === 'interno' && info.id === currentUser.id);
      return matchSala && matchUsuario;
    });
  }, [reservas, currentUser, filtroSala, esColaborador]);

  console.log("sssssss", reservasUsuario)

  const isAdmin = useMemo(() => {
    if (!currentUser) return false;
    return currentUser.rol?.toLowerCase() === 'administrador' ||
           currentUser.rol?.toLowerCase() === 'super administrador';
  }, [currentUser]);

  // Lista unificada internos+colaboradores para el selector de reasignacion (admin).
  useEffect(() => {
    if (isAdmin) {
      fetchPersonasReservables()
        .then(data => setReservables(Array.isArray(data) ? data : []))
        .catch(() => setReservables([]));
    }
  }, [isAdmin]);

  const personasFiltradas = useMemo(() => {
    const sortPersonas = (arr) => arr.sort((a, b) => `${a.nombre} ${a.apellidos}`.localeCompare(`${b.nombre} ${b.apellidos}`));

    if (!filtroSala) return sortPersonas([...personas]);

    const salaSeleccionada = salas.find(s => s.Id === parseInt(filtroSala));
    if (!salaSeleccionada) return sortPersonas([...personas]);

    const oficinaIdSala = salaSeleccionada.id_oficina || salaSeleccionada.Oficina?.Id;

    if (!oficinaIdSala) return sortPersonas([...personas]);

    return sortPersonas(personas.filter(persona => {
      if (persona.Oficina && persona.Oficina.Id === oficinaIdSala) {
        return true;
      }
      if (persona.Oficinas && Array.isArray(persona.Oficinas)) {
        return persona.Oficinas.some(oficina => oficina.Id === oficinaIdSala);
      }
      return false;
    }));
  }, [filtroSala, personas, salas]);

  // Calcular empresas disponibles según permisos (solo departamentos internos)
  const negociosDisponibles = useMemo(() => {
    if (esColaborador) {
      // El colaborador no elige empresa; su empresa (color) la resuelve el backend.
      return [];
    }
    if (isAdmin && !nuevaReserva.persona_id && !selectedEvent?.persona_id) {
      return negocios;
    }
    
    const personaIdAVerificar = selectedEvent?.persona_id || 
                                 (nuevaReserva.persona_id ? parseInt(nuevaReserva.persona_id) : null) || 
                                 currentUser?.id;
    
    if (!personaIdAVerificar) return negocios;
    
    const persona = personas.find(p => p.Id === personaIdAVerificar);
    if (!persona || !persona.Cargos || persona.Cargos.length === 0) {
      return [];
    }
    
    // Filtrar solo cargos con departamentos de tipo 'interno'
    const cargosInternos = persona.Cargos.filter(cargo => {
      return cargo.Departamento && cargo.Departamento.tipo === 'interno';
    });
    
    
    const empresaIds = [...new Set(cargosInternos.map(cargo => cargo.empresa_id).filter(Boolean))];
    
    return negocios.filter(e => empresaIds.includes(e.Id));
  }, [esColaborador, isAdmin, nuevaReserva.persona_id, selectedEvent?.persona_id, currentUser, personas, negocios]);

  // Negocios del reservador ACTUAL de la reserva en edicion, para que el color solo
  // pueda ser una empresa a la que el reservador pertenece. Se recalcula si se
  // reasigna la persona en el selector unificado (depende de selectedEvent.persona_id).
  const negociosEditables = useMemo(() => {
    if (!selectedEvent || selectedEvent.persona_colaboradora_id) return [];
    const persona = personas.find(p => p.Id === Number(selectedEvent.persona_id));
    if (!persona || !persona.Cargos || persona.Cargos.length === 0) return [];
    const cargosInternos = persona.Cargos.filter(c => c.Departamento && c.Departamento.tipo === 'interno');
    const empresaIds = [...new Set(cargosInternos.map(c => c.empresa_id).filter(Boolean))];
    return negocios.filter(e => empresaIds.includes(e.Id));
  }, [selectedEvent, personas, negocios]);

  const isOwner = (reserva) => {
    if (!currentUser || !reserva) return false;
    const info = getReservaOwnerInfo(reserva);
    const esPropia = esColaborador
      ? (info.tipo === 'colaborador' && info.id === currentUser.id)
      : (info.tipo === 'interno' && info.id === currentUser.id);
    return esPropia || isAdmin;
  };

  const handleSelectEvent = (event, e) => {
    setPopoverEvent(event);
    setAnchorEl(e.currentTarget);
    console.log(e)
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setPopoverEvent(null);
  };

  const handleEditFromPopover = () => {
    if (!isOwner(popoverEvent.resource)) {
      toast.error('No tienes permisos para editar esta reserva');
      return;
    }
    setSelectedEvent(popoverEvent.resource);
    setShowModal(true);
    handleClosePopover();
  };

  const handleDeleteFromPopover = async () => {
    if (!isOwner(popoverEvent.resource)) {
      toast.error('No tienes permisos para eliminar esta reserva');
      return;
    }
    try {
      await eliminarReserva(popoverEvent.resource.Id);
      const reservasData = await fetchReservas();
      setReservas(reservasData);
      handleClosePopover();
      toast.success('Reserva eliminada correctamente');
    } catch (error) {
      console.error('Error al eliminar reserva:', error);
      toast.error(error.response?.data?.message || 'Error al eliminar la reserva');
    }
  };

  const handleSelectSlot = ({ start, end }) => {
    setNuevaReserva({
      sala_id: filtroSala || '',
      empresa_id: '',
      persona_id: currentUser?.id?.toString() || '',
      persona_colaboradora_id: null,
      empresa_colaboradora_id: null,
      fecha: format(start, 'yyyy-MM-dd'),
      hora_inicio: format(start, 'HH:mm'),
      hora_fin: format(end, 'HH:mm'),
      observaciones: ''
    });
    setShowCreateModal(true);
  };

  const handleEventDrop = async ({ event, start, end }) => {
    if (!isOwner(event.resource)) {
      toast.error('No tienes permisos para editar esta reserva');
      return;
    }

    try {
      const reservaActualizada = {
        id: event.resource.Id,
        sala_id: event.resource.sala_id,
        persona_id: event.resource.persona_id,
        empresa_id: event.resource.empresa_id || null,
        fecha_inicio: start.toISOString(),
        fecha_fin: end.toISOString(),
        observaciones: event.resource.observaciones
      };
      
      // Actualizar estado localmente de inmediato
      setReservas(prevReservas => 
        prevReservas.map(reserva => 
          reserva.Id === event.resource.Id 
            ? { ...reserva, fecha_inicio: start.toISOString(), fecha_fin: end.toISOString() }
            : reserva
        )
      );
      
      await actualizarReserva(reservaActualizada);
      toast.success('Reserva editada correctamente');
    } catch (error) {
      console.error('Error al editar reserva:', error);
      toast.error(error.response?.data?.message || 'Error al editar la reserva');
      // Revertir cambios en caso de error
      const reservasData = await fetchReservas();
      setReservas(reservasData);
    }
  };

  const handleEventResize = async ({ event, start, end }) => {
    if (!isOwner(event.resource)) {
      toast.error('No tienes permisos para editar esta reserva');
      return;
    }

    try {
      const reservaActualizada = {
        id: event.resource.Id,
        sala_id: event.resource.sala_id,
        persona_id: event.resource.persona_id,
        empresa_id: event.resource.empresa_id || null,
        fecha_inicio: start.toISOString(),
        fecha_fin: end.toISOString(),
        observaciones: event.resource.observaciones
      };
      
      // Actualizar estado localmente de inmediato
      setReservas(prevReservas => 
        prevReservas.map(reserva => 
          reserva.Id === event.resource.Id 
            ? { ...reserva, fecha_inicio: start.toISOString(), fecha_fin: end.toISOString() }
            : reserva
        )
      );
      
      await actualizarReserva(reservaActualizada);
      toast.success('Reserva editada correctamente');
    } catch (error) {
      console.error('Error al editar la reserva:', error);
      toast.error(error.response?.data?.message || 'Error al editar la reserva');
      // Revertir cambios en caso de error
      const reservasData = await fetchReservas();
      setReservas(reservasData);
    }
  };

  const handleCrearReserva = async (e) => {
    e.preventDefault();
    try {
      if (!nuevaReserva.hora_inicio || !nuevaReserva.hora_fin) {
        toast.error('Por favor, ingrese horas de inicio y fin válidas');
        return;
      }
      
      // Combinar fecha con horas para crear las fechas completas
      const fechaInicio = new Date(`${nuevaReserva.fecha}T${nuevaReserva.hora_inicio}:00`);
      const fechaFin = new Date(`${nuevaReserva.fecha}T${nuevaReserva.hora_fin}:00`);
      
      // Validar que las fechas sean válidas
      if (isNaN(fechaInicio.getTime()) || isNaN(fechaFin.getTime())) {
        toast.error('Las horas ingresadas no son válidas');
        return;
      }
      
      // Validar que la hora de fin sea posterior a la de inicio
      if (fechaFin <= fechaInicio) {
        toast.error('La hora de fin debe ser posterior a la hora de inicio');
        return;
      }
      
      let reservaData;
      if (esColaborador) {
        // Colaborador (usuario actual): reserva a nombre propio. El backend resuelve empresa_colaboradora_id (color).
        reservaData = {
          sala_id: parseInt(nuevaReserva.sala_id),
          persona_id: null,
          persona_colaboradora_id: currentUser?.id,
          empresa_id: null,
          empresa_colaboradora_id: null,
          fecha_inicio: fechaInicio.toISOString(),
          fecha_fin: fechaFin.toISOString(),
          observaciones: nuevaReserva.observaciones
        };
      } else if (isAdmin && nuevaReserva.persona_colaboradora_id) {
        // Admin reserva a nombre de un COLABORADOR (elegido en el selector unificado).
        reservaData = {
          sala_id: parseInt(nuevaReserva.sala_id),
          persona_id: null,
          persona_colaboradora_id: parseInt(nuevaReserva.persona_colaboradora_id),
          empresa_id: null,
          empresa_colaboradora_id: nuevaReserva.empresa_colaboradora_id || null,
          fecha_inicio: fechaInicio.toISOString(),
          fecha_fin: fechaFin.toISOString(),
          observaciones: nuevaReserva.observaciones
        };
      } else {
        // Interno: admin a nombre de un interno (persona elegida) o el propio usuario.
        const personaId = (isAdmin && nuevaReserva.persona_id) ? parseInt(nuevaReserva.persona_id) : currentUser?.id;
        reservaData = {
          sala_id: parseInt(nuevaReserva.sala_id),
          persona_id: personaId,
          persona_colaboradora_id: null,
          empresa_id: nuevaReserva.empresa_id ? parseInt(nuevaReserva.empresa_id) : null,
          empresa_colaboradora_id: null,
          fecha_inicio: fechaInicio.toISOString(),
          fecha_fin: fechaFin.toISOString(),
          observaciones: nuevaReserva.observaciones
        };
      }
      await crearReserva(reservaData);
      // Solo recargar reservas en lugar de todo
      const reservasData = await fetchReservas();
      setReservas(reservasData);
      setShowCreateModal(false);
      setNuevaReserva({ sala_id: '', empresa_id: '', persona_id: '', persona_colaboradora_id: null, empresa_colaboradora_id: null, fecha: '', hora_inicio: '', hora_fin: '', observaciones: '' });
      toast.success('Reserva creada correctamente');
    } catch (error) {
      console.error('Error al crear reserva:', error);
      toast.error(error.response?.data?.message || 'Error al crear la reserva');
    }
  };

  const handleEditarReserva = async (e) => {
    e.preventDefault();
    try {
      // Combinar fecha con horas para actualizar
      const fechaInicio = new Date(selectedEvent.fecha_inicio);
      const fechaFin = new Date(selectedEvent.fecha_fin);
      
      // Validar que las fechas sean válidas
      if (isNaN(fechaInicio.getTime()) || isNaN(fechaFin.getTime())) {
        toast.error('Las horas ingresadas no son válidas');
        return;
      }
      
      // Validar que la hora de fin sea posterior a la de inicio
      if (fechaFin <= fechaInicio) {
        toast.error('La hora de fin debe ser posterior a la hora de inicio');
        return;
      }
      
      // Se preserva el tipo (interno/colaborador) de la reserva original; el dueño
      // no se cambia desde la edición en modo usuario normal.
      const ownerInfo = getReservaOwnerInfo(selectedEvent);
      const reservaActualizada = {
        id: selectedEvent.Id,
        sala_id: selectedEvent.sala_id,
        persona_id: ownerInfo.tipo === 'interno' ? ownerInfo.id : null,
        persona_colaboradora_id: ownerInfo.tipo === 'colaborador' ? ownerInfo.id : null,
        empresa_id: ownerInfo.tipo === 'interno' ? (selectedEvent.empresa_id || null) : null,
        empresa_colaboradora_id: ownerInfo.tipo === 'colaborador' ? (selectedEvent.empresa_colaboradora_id || null) : null,
        fecha_inicio: fechaInicio.toISOString(),
        fecha_fin: fechaFin.toISOString(),
        observaciones: selectedEvent.observaciones
      };
      
      await actualizarReserva(reservaActualizada);
      // Solo recargar reservas en lugar de todo
      const reservasData = await fetchReservas();
      setReservas(reservasData);
      setShowModal(false);
      toast.success('Reserva actualizada correctamente');
    } catch (error) {
      console.error('Error al actualizar reserva:', error);
      toast.error(error.response?.data?.message || 'Error al actualizar la reserva');
    }
  };

  const handleEliminarReserva = async () => {
    try {
      await eliminarReserva(selectedEvent.Id);
      // Solo recargar reservas en lugar de todo
      const reservasData = await fetchReservas();
      setReservas(reservasData);
      setShowModal(false);
      toast.success('Reserva eliminada correctamente');
    } catch (error) {
      console.error('Error al eliminar reserva:', error);
      toast.error(error.response?.data?.message || 'Error al eliminar la reserva');
    }
  };

  const handleEliminarReservaDirecta = async (id) => {
    try {
      await eliminarReserva(id);
      // Solo recargar reservas en lugar de todo
      const reservasData = await fetchReservas();
      setReservas(reservasData);
      toast.success('Reserva eliminada correctamente');
    } catch (error) {
      console.error('Error al eliminar reserva:', error);
      toast.error(error.response?.data?.message || 'Error al eliminar la reserva');
    }
  };

  const esColorClaro = (hexColor) => {
    if(hexColor === '#95a5a6') return false;
    // Convertir hex a RGB
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calcular luminancia (brillo percibido)
    const luminancia = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Si la luminancia es mayor a 0.5, el color es claro
    return luminancia > 0.5;
  };

  const eventStyleGetter = (event) => {
    const colorTexto = esColorClaro(event.color) ? '#000000' : '#ffffff';
    
    return {
      style: {
        backgroundColor: event.color,
        borderRadius: '5px',
        opacity: 0.8,
        color: colorTexto,
        border: '0px',
        display: 'block'
      }
    };
  };

  if (loading) {
    return (
      <div className="gestor-salas-loading">
        <div className="spinner"></div>
        <p>Cargando datos...</p>
      </div>
    );
  }

  if (!loading && salas.length === 0) {
    return (
      <div className="gestor-salas-container">
        <div className="tabs-container">
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Typography variant="h5" color="textSecondary" gutterBottom>
              No tienes acceso a ninguna sala
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Contacta con soporte para que te asigne permisos a las salas.
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  const openPopover = Boolean(anchorEl);

  return (
    <div className="gestor-salas-container">
      <div className="tabs-container">
        <div className="tabs-header">
          <div className="tabs-buttons">
            <button 
              className={`tab-button ${activeTab === 'calendario' ? 'active' : ''}`}
              onClick={() => setActiveTab('calendario')}
            >
              <CalendarTodayIcon sx={{ mr: 1, fontSize: 20 }} /> Calendario
            </button>
            <button 
              className={`tab-button ${activeTab === 'lista' ? 'active' : ''}`}
              onClick={() => setActiveTab('lista')}
            >
              <ListAltIcon sx={{ mr: 1, fontSize: 20 }} /> Lista de Reservas
            </button>
            <button 
              className={`tab-button ${activeTab === 'historial' ? 'active' : ''}`}
              onClick={() => setActiveTab('historial')}
            >
              <HistoryIcon sx={{ mr: 1, fontSize: 20 }} /> Mi Historial
            </button>
          </div>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => {
              setNuevaReserva({
                sala_id: filtroSala || '',
                empresa_id: '',
                persona_id: currentUser?.id?.toString() || '',
                fecha: '',
                hora_inicio: '',
                hora_fin: '',
                observaciones: ''
              });
              setShowCreateModal(true);
            }}
            sx={{ textTransform: 'none', backgroundColor: '#336385',  '&:hover': {
                backgroundColor: '#27496d',
                }}}
          >
            Nueva Reserva
          </Button>
        </div>

        <div className="tabs-content">
          {activeTab === 'calendario' && (
            <div className="calendario-tab">
              <div className="filtros-calendario">
                <FormControl size="small" variant="outlined" sx={{ minWidth: 200, marginTop: '5px' }}>
                  <InputLabel shrink>Sala</InputLabel>
                  <Select
                    value={filtroSala}
                    label="Sala"
                    notched
                    onChange={(e) => setFiltroSala(e.target.value)}
                  >
                    {salas.map(sala => (
                      <MenuItem key={sala.Id} value={sala.Id}>{sala.Oficina.nombre} - {sala.nombre}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 250 }}
                  options={personasFiltradas}
                  getOptionLabel={(option) => `${option.nombre} ${option.apellidos}`}
                  value={personasFiltradas.find(p => p.Id === parseInt(filtroPersona)) || null}
                  onChange={(event, newValue) => {
                    setFiltroPersona(newValue ? newValue.Id.toString() : '');
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Persona" variant="outlined" sx={{marginTop: '5px'}} />
                  )}
                  isOptionEqualToValue={(option, value) => option.Id === value.Id}
                  noOptionsText="No se encontraron personas"
                />
              </div>
              <DragAndDropCalendar
                localizer={localizer}
                events={events.filter(e => {
                  const matchSala = !filtroSala || e.resource.sala_id === parseInt(filtroSala);
                  const matchPersona = !filtroPersona || e.resource.persona_id === parseInt(filtroPersona);
                  return matchSala && matchPersona;
                })}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 'calc(100vh - 200px)' }}
                onSelectEvent={handleSelectEvent}
                onSelectSlot={handleSelectSlot}
                onEventDrop={handleEventDrop}
                onEventResize={handleEventResize}
                selectable
                resizable={view !== 'month'}
                draggableAccessor={(event) => isOwner(event.resource)}
                view={view}
                onView={setView}
                date={date}
                onNavigate={setDate}
                views={['month', 'week', 'day']}
                eventPropGetter={eventStyleGetter}
                components={{
                  event: ({ event }) => {
                    const isMobile = window.innerWidth <= 768;
                    const isMonthView = view === 'month';
                    
                    // En móvil y vista mensual, mostrar solo el nombre
                    if (isMobile && isMonthView) {
                      return (
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                          {event.title}
                        </div>
                      );
                    }
                    
                    // En otras vistas o desktop, mostrar nombre y horario
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 1 }}>
                          {event.title}
                        </span>
                        <span style={{ marginLeft: '4px', flexShrink: 0, whiteSpace: 'nowrap' }}>
                          ({event.horario})
                        </span>
                      </div>
                    );
                  }
                }}
                step={30}
                timeslots={2}
                messages={{
                  next: "Siguiente",
                  previous: "Anterior",
                  today: "Hoy",
                  month: "Mes",
                  week: "Semana",
                  day: "Día",
                  agenda: "Agenda",
                  date: "Fecha",
                  time: "Hora",
                  event: "Evento",
                  noEventsInRange: "No hay reservas en este rango",
                  showMore: total => `+ Ver más (${total})`
                }}
                culture="es"
              />
            </div>
          )}

          {activeTab === 'lista' && (
            <div className="lista-tab">
              <div className="filtros-lista">
                <FormControl size="small" variant="outlined" sx={{ minWidth: 200, marginTop: '5px' }}>
                  <InputLabel shrink>Sala</InputLabel>
                  <Select
                    value={filtroSala}
                    label="Sala"
                    notched
                    onChange={(e) => setFiltroSala(e.target.value)}
                  >
                    {salas.map(sala => (
                      <MenuItem key={sala.Id} value={sala.Id}>{sala.Oficina.nombre} - {sala.nombre}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 250 }}
                  options={personasFiltradas}
                  getOptionLabel={(option) => `${option.nombre} ${option.apellidos}`}
                  value={personasFiltradas.find(p => p.Id === parseInt(filtroPersona)) || null}
                  onChange={(event, newValue) => {
                    setFiltroPersona(newValue ? newValue.Id.toString() : '');
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Persona" variant="outlined" sx={{marginTop: '5px'}} />
                  )}
                  isOptionEqualToValue={(option, value) => option.Id === value.Id}
                  noOptionsText="No se encontraron personas"
                />
              </div>
              <div className="reservas-lista">
                {reservasFiltradas.length === 0 ? (
                  <p className="no-reservas">No hay reservas que mostrar</p>
                ) : (
                  reservasFiltradas.map(reserva => (
                    <div key={reserva.Id} className="reserva-card">
                      <div className="reserva-body">
                        <div className="reserva-actions" style={{ position: 'absolute', top: '10px', right: '10px' }}>
                          {isOwner(reserva) && (
                            <>
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => {
                                  setSelectedEvent(reserva);
                                  setShowModal(true);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleEliminarReservaDirecta(reserva.Id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </div>
                        <p><strong>Persona:</strong> {getReservaOwnerInfo(reserva).nombre} {getReservaOwnerInfo(reserva).apellidos}</p>
                        {reserva.empresa_colaboradora_id
                          ? <p><strong>Negocio:</strong> {empresasColaboradoras.find(e => e.Id === reserva.empresa_colaboradora_id)?.nombre || 'Sin especificar'}</p>
                          : (reserva.empresa_id && <p><strong>Negocio:</strong> {negocios.find(e => e.Id === reserva.empresa_id)?.nombre || 'Sin especificar'}</p>)}
                        <p><strong>Fecha:</strong> {format(parseISO(reserva.fecha_inicio), 'dd/MM/yyyy', { locale: es })}</p>
                        <p><strong>Horario:</strong> {format(parseISO(reserva.fecha_inicio), 'HH:mm', { locale: es })} - {format(parseISO(reserva.fecha_fin), 'HH:mm', { locale: es })}</p>
                        {reserva.observaciones && <p><strong>Observaciones:</strong> {reserva.observaciones}</p>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'historial' && (
            <div className="historial-tab">
              {/* <h2>Mis Reservas</h2> */}
              <div className="filtros-lista" style={{ marginBottom: '20px' }}>
                <FormControl size="small" variant="outlined" sx={{ minWidth: 200, marginTop: '5px' }}>
                  <InputLabel shrink>Sala</InputLabel>
                  <Select
                    value={filtroSala || salas[0]?.Id || ''}
                    label="Sala"
                    onChange={(e) => setFiltroSala(e.target.value)}
                    notched
                  >
                    {salas.map(sala => (
                      <MenuItem key={sala.Id} value={sala.Id}>{sala.Oficina.nombre} - {sala.nombre}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div className="reservas-lista">
                {reservasUsuario.length === 0 ? (
                  <p className="no-reservas">No tienes reservas</p>
                ) : (
                  reservasUsuario.map(reserva => (
                    <div key={reserva.Id} className="reserva-card">
                      <div className="reserva-body">
                        <div className="reserva-actions" style={{ position: 'absolute', top: '10px', right: '10px' }}>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => {
                              setSelectedEvent(reserva);
                              setShowModal(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleEliminarReservaDirecta(reserva.Id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </div>
                        <p><strong>Fecha:</strong> {format(parseISO(reserva.fecha_inicio), 'dd/MM/yyyy', { locale: es })}</p>
                        {reserva.empresa_id && <p><strong>Negocio:</strong> {negocios.find(e => e.Id === reserva.empresa_id)?.nombre || 'Sin especificar'}</p>}
                        <p><strong>Horario:</strong> {format(parseISO(reserva.fecha_inicio), 'HH:mm', { locale: es })} - {format(parseISO(reserva.fecha_fin), 'HH:mm', { locale: es })}</p>
                        {reserva.observaciones && <p><strong>Observaciones:</strong> {reserva.observaciones}</p>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Popover de información del evento */}
      <Popover
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {popoverEvent && (
          <Card sx={{ minWidth: 380, maxWidth: 450 }}>
            <CardContent sx={{ pb: 2, position: 'relative' }}>
              {/* Logo de la empresa en la esquina superior derecha */}
              {popoverEvent.Negocio?.logo && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    width: 60,
                    height: 60,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <img
                    src={`${process.env.REACT_APP_API_URL}/api/negocio/logo/${popoverEvent.Negocio.logo}`}
                    alt={popoverEvent.Negocio.nombre}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </Box>
              )}
              
              <Typography 
                variant="h6" 
                component="div" 
                sx={{ 
                  color: popoverEvent.Negocio?.color || '#95a5a6',
                  fontWeight: 'bold',
                  mb: 2.5,
                  pr: popoverEvent.Negocio?.logo ? 8 : 0
                }}
              >
                {salas.find(s => s.Id === popoverEvent.resource?.sala_id)?.nombre || 'Sala desconocida'}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon fontSize="small" sx={{ color: '#666' }} />
                <span><strong>Persona:</strong> {getReservaOwnerInfo(popoverEvent.resource).nombre} {getReservaOwnerInfo(popoverEvent.resource).apellidos}</span>
              </Typography>

              {(popoverEvent.resource?.empresa_id || popoverEvent.resource?.empresa_colaboradora_id) && (
                <Typography variant="body2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon fontSize="small" sx={{ color: '#666' }} />
                  <span><strong>Negocio:</strong> {
                    popoverEvent.resource?.empresa_colaboradora_id
                      ? (empresasColaboradoras.find(e => e.Id === popoverEvent.resource?.empresa_colaboradora_id)?.nombre || 'Sin especificar')
                      : (negocios.find(e => e.Id === popoverEvent.resource?.empresa_id)?.nombre || 'Sin especificar')
                  }</span>
                </Typography>
              )}
              
              <Typography variant="body2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventIcon fontSize="small" sx={{ color: '#666' }} />
                <span><strong>Fecha:</strong> {format(parseISO(popoverEvent.resource?.fecha_inicio), 'dd/MM/yyyy', { locale: es })}</span>
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon fontSize="small" sx={{ color: '#666' }} />
                <span>
                  <strong>Horario:</strong> {format(parseISO(popoverEvent.resource?.fecha_inicio), 'HH:mm', { locale: es })} - {format(parseISO(popoverEvent.resource?.fecha_fin), 'HH:mm', { locale: es })}
                </span>
              </Typography>
              
              {popoverEvent.resource?.observaciones && (
                <Typography variant="body2" sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <NotesIcon fontSize="small" sx={{ color: '#666', mt: 0.3 }} />
                  <span><strong>Observaciones:</strong> {popoverEvent.resource?.observaciones}</span>
                </Typography>
              )}
              
              {isOwner(popoverEvent.resource) ? (
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleEditFromPopover}
                    startIcon={<EditIcon />}
                    fullWidth
                    size="small"
                    sx={{"backgroundColor": "#336385", color: 'white', '&:hover': { backgroundColor: '#27496d' }}}
                  >
                    Editar
                  </Button>
                  <Button 
                    variant="contained" 
                    color="error" 
                    onClick={handleDeleteFromPopover}
                    startIcon={<DeleteIcon />}
                    fullWidth
                    size="small"
                  >
                    Eliminar
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        )}
      </Popover>

      {/* Dialog de Edición */}
      <Dialog 
        open={showModal} 
        onClose={() => setShowModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#336385', color: 'white', '&:hover': { backgroundColor: '#27496d' } }}>Editar Reserva</DialogTitle>
        <form onSubmit={handleEditarReserva}>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel shrink>Sala</InputLabel>
                <Select
                  value={selectedEvent?.sala_id || ''}
                  label="Sala"
                  onChange={(e) => setSelectedEvent({...selectedEvent, sala_id: parseInt(e.target.value)})}
                  required
                  notched
                >
                  {salas.map(sala => (
                    <MenuItem key={sala.Id} value={sala.Id}>{sala.Oficina.nombre} - {sala.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!selectedEvent?.persona_colaboradora_id && (
              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel shrink>Negocio</InputLabel>
                <Select
                  value={selectedEvent?.empresa_id || ''}
                  label="Negocio"
                  onChange={(e) => setSelectedEvent({...selectedEvent, empresa_id: e.target.value ? parseInt(e.target.value) : null})}
                  notched
                  displayEmpty
                >
                  <MenuItem value="">Ninguna</MenuItem>
                  {negociosEditables.map(negocio => (
                    <MenuItem key={negocio.Id} value={negocio.Id}>{negocio.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              )}
            </Box>
            {isAdmin ? (
              <Autocomplete
                fullWidth
                size="medium"
                sx={{ mt: 2, mb: 1 }}
                options={reservables}
                getOptionLabel={(o) => `${o.nombre} ${o.apellidos}${o.tipo === 'colaborador' ? ' (Colaborador)' : ''}`}
                value={reservables.find(o => {
                  const info = getReservaOwnerInfo(selectedEvent);
                  return o.tipo === info.tipo && Number(o.id) === Number(info.id);
                }) || null}
                onChange={(event, nv) => {
                  if (!nv) {
                    setSelectedEvent({ ...selectedEvent, persona_id: null, persona_colaboradora_id: null });
                  } else if (nv.tipo === 'colaborador') {
                    setSelectedEvent({ ...selectedEvent, persona_colaboradora_id: nv.id, persona_id: null, empresa_id: null, empresa_colaboradora_id: nv.empresa_colaboradora_id || null });
                  } else {
                    // Si cambia el reservador interno, se limpia el negocio (debe elegirse uno del nuevo reservador).
                    const cambiaReservador = !!selectedEvent?.persona_colaboradora_id || Number(nv.id) !== Number(selectedEvent?.persona_id);
                    setSelectedEvent({ ...selectedEvent, persona_id: nv.id, persona_colaboradora_id: null, empresa_colaboradora_id: null, ...(cambiaReservador ? { empresa_id: null } : {}) });
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Persona" variant="outlined" required />
                )}
                isOptionEqualToValue={(option, value) => option.tipo === value.tipo && option.id === value.id}
                noOptionsText="No se encontraron personas"
              />
            ) : (
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                label="Persona"
                value={selectedEvent ? `${getReservaOwnerInfo(selectedEvent).nombre} ${getReservaOwnerInfo(selectedEvent).apellidos}`.trim() : ''}
                InputProps={{ readOnly: true }}
                disabled
              />
            )}
            <TextField
              fullWidth
              margin="normal"
              label="Fecha"
              type="date"
              value={selectedEvent ? format(parseISO(selectedEvent.fecha_inicio), "yyyy-MM-dd") : ''}
              onChange={(e) => {
                const fechaInicio = parseISO(selectedEvent.fecha_inicio);
                const fechaFin = parseISO(selectedEvent.fecha_fin);
                const nuevaFechaInicio = new Date(`${e.target.value}T${format(fechaInicio, 'HH:mm')}:00`);
                const nuevaFechaFin = new Date(`${e.target.value}T${format(fechaFin, 'HH:mm')}:00`);
                setSelectedEvent({
                  ...selectedEvent, 
                  fecha_inicio: nuevaFechaInicio.toISOString(),
                  fecha_fin: nuevaFechaFin.toISOString()
                });
              }}
              InputLabelProps={{ shrink: true }}
              required
            />
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <TimePicker
                label="Hora Inicio"
                value={selectedEvent ? parseISO(selectedEvent.fecha_inicio) : null}
                onChange={(newValue) => {
                  if (newValue && !isNaN(newValue.getTime())) {
                    const fechaActual = format(parseISO(selectedEvent.fecha_inicio), "yyyy-MM-dd");
                    const nuevaFecha = new Date(`${fechaActual}T${format(newValue, 'HH:mm')}:00`);
                    setSelectedEvent({...selectedEvent, fecha_inicio: nuevaFecha.toISOString()});
                  }
                }}
                onError={() => {}}
                ampm={false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    margin="normal"
                    required
                    error={false}
                    helperText=""
                  />
                )}
              />
              <TimePicker
                label="Hora Fin"
                value={selectedEvent ? parseISO(selectedEvent.fecha_fin) : null}
                onChange={(newValue) => {
                  if (newValue && !isNaN(newValue.getTime())) {
                    const fechaActual = format(parseISO(selectedEvent.fecha_inicio), "yyyy-MM-dd");
                    const nuevaFecha = new Date(`${fechaActual}T${format(newValue, 'HH:mm')}:00`);
                    setSelectedEvent({...selectedEvent, fecha_fin: nuevaFecha.toISOString()});
                  }
                }}
                onError={() => {}}
                ampm={false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    margin="normal"
                    required
                    error={false}
                    helperText=""
                  />
                )}
              />
            </LocalizationProvider>
            <TextField
              fullWidth
              margin="normal"
              label="Observaciones"
              multiline
              rows={3}
              value={selectedEvent?.observaciones || ''}
              onChange={(e) => setSelectedEvent({...selectedEvent, observaciones: e.target.value})}
            />
          </DialogContent>
          <DialogActions>
            <Button sx={{"color": "#000"}} onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleEliminarReserva} color="error" startIcon={<DeleteIcon />}>
              Eliminar
            </Button>
            <Button sx={{"backgroundColor": "#336385", '&:hover': { backgroundColor: '#27496d' }}} type="submit" variant="contained" startIcon={<EditIcon />}>
              Guardar Cambios
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog de Crear Reserva */}
      <Dialog 
        open={showCreateModal} 
        onClose={() => setShowCreateModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#336385', color: 'white', '&:hover': { backgroundColor: '#27496d' } }}>Nueva Reserva</DialogTitle>
        <form onSubmit={handleCrearReserva}>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel shrink>Sala *</InputLabel>
                <Select
                  value={nuevaReserva.sala_id}
                  label="Sala *"
                  onChange={(e) => setNuevaReserva({...nuevaReserva, sala_id: e.target.value})}
                  required
                  notched
                >
                  {salas.map(sala => (
                    <MenuItem key={sala.Id} value={sala.Id}>{sala.Oficina.nombre} - {sala.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!esColaborador && !nuevaReserva.persona_colaboradora_id && (
              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel shrink>Negocio</InputLabel>
                <Select
                  value={nuevaReserva.empresa_id}
                  label="Negocio"
                  onChange={(e) => setNuevaReserva({...nuevaReserva, empresa_id: e.target.value})}
                  notched
                  displayEmpty
                >
                  <MenuItem value="">Ninguna</MenuItem>
                  {negociosDisponibles.map(negocio => (
                    <MenuItem key={negocio.Id} value={negocio.Id}>{negocio.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              )}
            </Box>
            {isAdmin && (
              <Autocomplete
                fullWidth
                size="medium"
                sx={{ mt: 2, mb: 1 }}
                options={reservables}
                getOptionLabel={(o) => `${o.nombre} ${o.apellidos}${o.tipo === 'colaborador' ? ' (Colaborador)' : ''}`}
                value={reservables.find(o => {
                  const selTipo = nuevaReserva.persona_colaboradora_id ? 'colaborador' : 'interno';
                  const selId = nuevaReserva.persona_colaboradora_id || nuevaReserva.persona_id;
                  return selId && o.tipo === selTipo && Number(o.id) === Number(selId);
                }) || null}
                onChange={(event, nv) => {
                  if (!nv) {
                    setNuevaReserva({ ...nuevaReserva, persona_id: '', persona_colaboradora_id: null });
                  } else if (nv.tipo === 'colaborador') {
                    setNuevaReserva({ ...nuevaReserva, persona_colaboradora_id: nv.id, persona_id: '', empresa_id: '', empresa_colaboradora_id: nv.empresa_colaboradora_id || null });
                  } else {
                    // Si cambia el reservador interno, se limpia el negocio (debe elegirse uno del nuevo reservador).
                    const cambia = !!nuevaReserva.persona_colaboradora_id || Number(nv.id) !== Number(nuevaReserva.persona_id);
                    setNuevaReserva({ ...nuevaReserva, persona_id: nv.id.toString(), persona_colaboradora_id: null, empresa_colaboradora_id: null, ...(cambia ? { empresa_id: '' } : {}) });
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Persona" variant="outlined" />
                )}
                isOptionEqualToValue={(option, value) => option.tipo === value.tipo && option.id === value.id}
                noOptionsText="No se encontraron personas"
              />
            )}
            <TextField
              fullWidth
              margin="normal"
              label="Fecha"
              type="date"
              value={nuevaReserva.fecha}
              onChange={(e) => setNuevaReserva({...nuevaReserva, fecha: e.target.value})}
              InputLabelProps={{ shrink: true }}
              required
            />
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <TimePicker
                label="Hora Inicio"
                value={nuevaReserva.hora_inicio ? parse(nuevaReserva.hora_inicio, 'HH:mm', new Date()) : null}
                onChange={(newValue) => {
                  if (newValue && !isNaN(newValue.getTime())) {
                    setNuevaReserva({...nuevaReserva, hora_inicio: format(newValue, 'HH:mm')});
                  }
                }}
                onError={() => {}}
                ampm={false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    margin="normal"
                    required
                    error={false}
                    helperText=""
                  />
                )}
              />
              <TimePicker
                label="Hora Fin"
                value={nuevaReserva.hora_fin ? parse(nuevaReserva.hora_fin, 'HH:mm', new Date()) : null}
                onChange={(newValue) => {
                  if (newValue && !isNaN(newValue.getTime())) {
                    setNuevaReserva({...nuevaReserva, hora_fin: format(newValue, 'HH:mm')});
                  }
                }}
                onError={() => {}}
                ampm={false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    margin="normal"
                    required
                    error={false}
                    helperText=""
                  />
                )}
              />
            </LocalizationProvider>
            <TextField
              fullWidth
              margin="normal"
              label="Observaciones"
              multiline
              rows={3}
              value={nuevaReserva.observaciones}
              onChange={(e) => setNuevaReserva({...nuevaReserva, observaciones: e.target.value})}
            />
          </DialogContent>
          <DialogActions>
            <Button sx={{"color": "black"}} onClick={() => setShowCreateModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" sx={{"backgroundColor": "#336385", 'color': 'white', '&:hover': { backgroundColor: '#27496d' }}} variant="contained" startIcon={<AddIcon />}>
              Crear Reserva
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Toast Container para notificaciones */}
      <ToastContainer 
        /*position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover*/
      />
    </div>
  );
}

export default GestorSalasReservas;
